import React, { useEffect, useState } from "react";
import { X, Download, Loader2, FileWarning } from "lucide-react";
import { api, documentFileURL } from "../lib/api";
import { toast } from "sonner";

/**
 * In-browser document preview modal. Uses an authenticated fetch so that the
 * HttpOnly auth cookie is forwarded via axios; for PDFs we turn the response
 * into a blob URL and render it inside an <iframe>.
 */
export default function DocumentPreviewModal({ document: doc, onClose }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const [text, setText] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const ct = (doc?.content_type || "").toLowerCase();
    const isPdf = ct.includes("pdf") || (doc?.filename || "").toLowerCase().endsWith(".pdf");
    const isText =
        ct.startsWith("text/") ||
        /\.(txt|md|csv|json)$/i.test(doc?.filename || "");
    const isDocx =
        ct.includes("wordprocessingml") ||
        /\.docx?$/i.test(doc?.filename || "");

    useEffect(() => {
        if (!doc) return;
        let cancelled = false;
        let createdUrl = null;
        setLoading(true);
        setError(null);
        setBlobUrl(null);
        setText(null);

        (async () => {
            try {
                const res = await api.get(`/documents/${doc.id}/file`, {
                    responseType: "blob",
                });
                if (cancelled) return;
                if (isText) {
                    const t = await res.data.text();
                    setText(t);
                } else {
                    createdUrl = URL.createObjectURL(res.data);
                    setBlobUrl(createdUrl);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(
                        e?.response?.status === 404
                            ? "Original file isn't stored for this document (seeded or legacy). Run Upload to attach a file."
                            : "Preview failed to load.",
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [doc, isText]);

    const handleDownload = async () => {
        try {
            const res = await api.get(`/documents/${doc.id}/file?download=1`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = window.document.createElement("a");
            a.href = url;
            a.download = doc.filename || "document";
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Download failed");
        }
    };

    if (!doc) return null;

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            data-testid="doc-preview-modal"
            onClick={onClose}
        >
            <div
                className="bg-white w-full max-w-5xl h-[85vh] border border-hair flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-3 border-b border-hair">
                    <div className="min-w-0">
                        <div className="overline">
                            {doc.doc_type || "Document"} · rev {doc.revision || 1}
                        </div>
                        <div className="heading font-medium truncate text-sm mt-1">
                            {doc.filename}
                        </div>
                    </div>
                    <div className="flex gap-2 items-center">
                        <button
                            data-testid="btn-download-preview"
                            onClick={handleDownload}
                            className="inline-flex items-center gap-2 border border-hair text-xs px-3 py-2 rounded-sm hover:bg-neutral-50"
                        >
                            <Download size={13} /> Download
                        </button>
                        <button
                            data-testid="btn-close-preview"
                            onClick={onClose}
                            className="w-8 h-8 inline-flex items-center justify-center border border-hair rounded-sm hover:bg-neutral-50"
                        >
                            <X size={14} />
                        </button>
                    </div>
                </div>

                <div className="flex-1 min-h-0 bg-neutral-100">
                    {loading && (
                        <div className="h-full flex items-center justify-center text-neutral-500 text-sm gap-2">
                            <Loader2 className="animate-spin" size={16} /> Loading
                            preview…
                        </div>
                    )}
                    {!loading && error && (
                        <div className="h-full flex flex-col items-center justify-center text-neutral-600 text-center p-10">
                            <FileWarning className="text-neutral-400" size={32} />
                            <p className="text-sm mt-4 max-w-md">{error}</p>
                        </div>
                    )}
                    {!loading && !error && isPdf && blobUrl && (
                        <iframe
                            data-testid="doc-preview-iframe"
                            title={`preview-${doc.filename}`}
                            src={blobUrl}
                            className="w-full h-full bg-white"
                        />
                    )}
                    {!loading && !error && isText && text != null && (
                        <pre
                            data-testid="doc-preview-text"
                            className="w-full h-full overflow-auto bg-white p-6 text-[13px] leading-relaxed whitespace-pre-wrap font-mono"
                        >
                            {text}
                        </pre>
                    )}
                    {!loading && !error && !isPdf && !isText && (
                        <div className="h-full flex flex-col items-center justify-center text-neutral-600 text-center p-10">
                            <FileWarning className="text-neutral-400" size={32} />
                            <p className="text-sm mt-4 max-w-md">
                                {isDocx
                                    ? "DOCX in-browser preview isn't supported yet. Please download the file to view it."
                                    : "This file type cannot be previewed inline. Download to view."}
                            </p>
                            <button
                                onClick={handleDownload}
                                className="mt-5 inline-flex items-center gap-2 bg-[#C2410C] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#9A3412]"
                            >
                                <Download size={13} /> Download file
                            </button>
                        </div>
                    )}
                </div>

                <div className="px-5 py-2 border-t border-hair mono text-[11px] text-neutral-500 flex items-center justify-between gap-4 flex-wrap">
                    <span className="truncate" title={doc.sha256}>
                        SHA-256 {doc.sha256 ? doc.sha256.slice(0, 24) + "…" : "—"}
                    </span>
                    <span>
                        {doc.size ? (doc.size / 1024).toFixed(1) + " KB" : ""}
                        {doc.uploaded_at
                            ? ` · ${new Date(doc.uploaded_at).toLocaleString()}`
                            : ""}
                    </span>
                </div>
            </div>
        </div>
    );
}
